const isEmpty = require('lodash/isEmpty');
const get = require('lodash/get');
const omit = require('lodash/omit');
const omitBy = require('lodash/omitBy');
const isNil = require('lodash/isNil');

const PRODUCT_ADDON_TYPES = ['giftwrap', 'postcard', 'greetingscard', 'stickers'];
const SINGLE_ADDON_TYPES = ['giftwrap'];
const GIFTWRAP_ADDON_TYPES = ['giftwrap', 'postcard'];
const EXTRA_ADDON_TYPES = ['greetingscard', 'stickers'];

const getAddonData = (addOn, variant) => variant.components.find(item => {
  // The monkey giftwrap id is the only id that has a different structure to other addons
  // where the structure for all other addons are structured as type:size:design, LMN's monkey addon
  // is structured book:type
  if (item.id === 'lmn:giftwrap') {
    return addOn.design === 'monkey' && addOn.type === 'giftwrap';
  }
  return item.id.includes(addOn.design) && item.id.includes(addOn.type);
});

const addOnAvailable = (addOn, variant) => {
  const isAvailable = PRODUCT_ADDON_TYPES.includes(addOn.type) ? !!getAddonData(addOn, variant) : true;
  const isVisible = addOn.visibleOn ? addOn.visibleOn.includes(variant.formatId) : true;

  return isAvailable && isVisible;
};

const findVariantById = (id, products) => {
  if (!id && isEmpty(products)) {
    return {};
  }
  const productId = id.split(':')[0];
  const product = products.find(product => product.id === productId) || {};
  return product.variants ? product.variants.find(variant => variant.id === id) : product;
};

const omitNilAddOnValues = addOn => omitBy(addOn, value => isNil(value) || isEmpty(value));

const hasAddons = pages => {
  return pages.length > 0 && pages[0].items.length > 0 && !isEmpty(omitNilAddOnValues(pages[0].items[0]));
};

const findAddOnContent = (addOn, addOns) => addOns.reduce((result, item) => {
  if (!isEmpty(result)) {
    return result;
  }
  if (item.type === addOn.type && item.design === addOn.design) {
    let description = isEmpty(get(addOn, 'description[0].text', '')) ? item.description : addOn.description;

    if (typeof description === 'string') {
      description = [{
        type: 'paragraph',
        text: description,
        spans: [],
      }];
    }

    return {
      ...omit(item, ['formatId']), // TODO: Remove this old key that is no longer used in prismic
      ...addOn,
      description
    };
  }
}, {});

const buildPages = product => {
  const pages = get(product, 'addOns.body', []);

  if (!hasAddons(pages)) {
    return [];
  }

  const { addOns } = product;
  const defaultEditingBanner = get(addOns, 'options.defaultAddOns.editingBanner', '');
  const defaultContent = get(addOns, 'options.defaultAddOns.content', []);
  const defaultButton = get(addOns, 'options.defaultAddOns.buttonCta', '');
  const defaultAddOns = get(addOns, 'options.defaultAddOns.body[0].items', []);

  return pages
    .filter(page => page.sliceType === 'page')
    .reduce((result, page, idx) => {
      // If the first page is empty/broken then we dont want to build other pages
      if (idx > 0 && isEmpty(result)) {
        return [];
      }

      const isPageContentEmpty = isEmpty(page.primary.content) || !page.primary.content[0].text;

      const editingBanner = !product.addOns.editBanner ? defaultEditingBanner : product.addOns.editBanner;
      const content = isPageContentEmpty ? defaultContent : page.primary.content;
      const buttonCta = !page.primary.buttonCta ? defaultButton : page.primary.buttonCta;

      const items = page.items
        .map(addOn => omitBy(addOn, value => isNil(value) || isEmpty(value)))
        .reduce((result, addOn) => {
          // Make sure we remove any keys that dont have any value or if there is a description with no value
          addOn = omitBy(addOn, (value, key) =>
            isNil(value) ||
            isEmpty(value) ||
            (key === 'description' && !value[0].text)
          );

          if (addOn.type && addOn.design) {
            const defaultAddOn = findAddOnContent(addOn, defaultAddOns);

            if (isEmpty(defaultAddOn) && !addOn.title && !addOn.description && !addOn.image) {
              return result;
            }

            let content = {
              ...defaultAddOn,
              ...addOn,
              lookInside: get(addOn, 'lookInside.content', {})
            };

            if (!isEmpty(content.lookInside)) {
              // Sometimes it seems Prismic returns a null slide value, here we are making sure that if it does do this
              // or if a content manager adds an empty slide then we remove it just in case
              content.lookInside.slider = content.lookInside.slider
                .filter(slide => !isNil(slide.image) && !!slide.image.url)
                .map(slide => slide.image);

            }

            content = omitBy(content, value => isNil(value) || isEmpty(value));

            if (!isEmpty(content)) {
              result.push(content);
            }
          }
          return result;
        }, []);

      if (!isEmpty(items)) {
        result.push({
          ...page,
          primary: {
            ...page.primary,
            editingBanner,
            content,
            buttonCta
          },
          items,
        });
      }

      return result;
    }, []);
};

const buildAddons = (variant, content, products = []) => {
  const pages = buildPages(content);

  if (isEmpty(pages)) {
    return [];
  }
  return pages.reduce((res, page) => {
    const addOns = page.items.reduce((result, addOn) => {
      if (!PRODUCT_ADDON_TYPES.includes(addOn.type)) {
        let variant = {};

        switch (addOn.type) {
          case 'book':
            variant = findVariantById(addOn.design, products);

            if (!variant || isEmpty(variant)) {
              return result;
            }
            break;
        }

        result.push({
          ...variant,
          ...addOn,
        });

        return result;
      }

      if (addOnAvailable(addOn, variant)) {
        result.push({
          ...getAddonData(addOn, variant),
          ...addOn,
        });
      }

      return result;
    }, []);

    if (!isEmpty(addOns)) {
      res.push({
        ...page,
        items: addOns
      });
    }

    return res;
  }, []);
};

const getPageIndex = (pages = [], addonTypes = []) => {
  let pageIdx = -1;
  pages.some((page, idx) => {
    const hasGiftwrap = page.items.some(item => addonTypes.includes(item.type));
    if (hasGiftwrap) {
      pageIdx = idx;
      return true;
    }
  });
  return pageIdx;
};

const getAddonsOfType = (addons, types) => {
  if ((!addons || isEmpty(addons)) && (!types || isEmpty(types))) {
    return [];
  }
  return addons.filter(addon => types.some(type => addon.id.includes(type)));
};

const getGiftwrapIndex = pages => {
  let pageIdx = getPageIndex(pages, GIFTWRAP_ADDON_TYPES);
  return pageIdx === -1 ? null : pageIdx;
};

const getExtrasIndex = pages => {
  let pageIdx = getPageIndex(pages, EXTRA_ADDON_TYPES);
  return pageIdx === -1 ? null : pageIdx;
};

module.exports = {
  PRODUCT_ADDON_TYPES,
  SINGLE_ADDON_TYPES,
  GIFTWRAP_ADDON_TYPES,
  EXTRA_ADDON_TYPES,
  getAddonsOfType,
  getAddonData,
  addOnAvailable,
  findVariantById,
  omitNilAddOnValues,
  hasAddons,
  findAddOnContent,
  getGiftwrapIndex,
  getExtrasIndex,
  buildAddons,
  buildPages,
};



// WEBPACK FOOTER //
// ./src/common/utils/addons.js